import React, { useCallback } from 'react'
import { Collapse } from '@mui/material'
import { isString } from '../../../utils'
import useId from '../../hooks/useId'
import Icon from '../Icon'
import {
  AlertMessage,
  AlertWrapper,
  AlertTitle,
  AlertButton,
  AlertIconButton
} from './Alert.styles'
import { AlertProps } from './Alert.types'

const Alert = React.forwardRef(
  (
    {
      className = '',
      show = true,
      title,
      message,
      icon,
      styles,
      closeButton,
      data,
      variant = 'outlined',
      'data-testid': testid = 'alert',
      customIcon,
      ...props
    }: AlertProps,
    ref
  ) => {
    const { getTestId } = useId(testid)
    const titlePros = isString(title) ? { value: title } : title
    const messageProps = isString(message) ? { value: message } : message
    const iconProps = isString(icon) ? { name: icon } : icon
    const alertICon = icon ? <Icon {...iconProps} /> : icon
    const { actions, button, iconButton } = closeButton || {}

    const handleClick = useCallback(
      (event: React.SyntheticEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        if (actions) {
          actions({ event, data })
        }
      },
      [actions, data]
    )

    const actionButton =
      (button && (
        <AlertButton
          data-testid={getTestId('action')}
          {...button}
          onClick={handleClick}
        />
      )) ||
      (iconButton && (
        <AlertIconButton
          data-testid={getTestId('action')}
          {...iconButton}
          onClick={handleClick}
        />
      ))

    const alert = (
      <AlertWrapper
        className={`Alert ${className}`}
        icon={customIcon || alertICon}
        sx={styles}
        action={actionButton}
        variant={variant}
        data-testid={getTestId()}
        {...props}>
        {title && (
          <AlertTitle data-testid={getTestId('title')} {...titlePros} />
        )}
        {message && (
          <AlertMessage data-testid={getTestId('message')} {...messageProps} />
        )}
      </AlertWrapper>
    )

    return (
      <Collapse in={show} ref={ref}>
        {alert}
      </Collapse>
    )
  }
)

Alert.displayName = 'Alert'
export default Alert
